import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { cn } from '../../utils/cn';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../form/form-primitives';
import { Stack } from '../stack/stack';

type FormTextFieldProps<TFieldValues extends FieldValues> = {
  label: string;
  inputName: FieldPath<TFieldValues>;
  placeholder?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
  disablePasswordManager?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const FormTextField = <TFieldValues extends FieldValues>({
  label,
  inputName,
  placeholder,
  isOptional = false,
  isDisabled = false,
  disablePasswordManager = false,
  onFocus = () => {},
  onBlur = () => {},
}: FormTextFieldProps<TFieldValues>) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <Stack space={1} align="stretch">
            <FormItem
              className={cn(
                'rounded-lg border border-border bg-white px-4 py-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:outline-hidden has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-muted',
                { 'border-destructive': !!formState.errors[inputName] },
              )}
            >
              <Stack space={1} align="stretch">
                <FormLabel className="text-xs font-medium text-muted-foreground">
                  {label}
                  {isOptional && <span className="text-inherit"> (Optional)</span>}
                </FormLabel>

                <FormControl>
                  <input
                    type="text"
                    placeholder={placeholder}
                    className="font-base w-full font-medium text-black placeholder:text-muted-foreground focus-visible:outline-hidden disabled:cursor-not-allowed disabled:bg-muted disabled:placeholder:text-muted-foreground/80"
                    aria-required={!isOptional}
                    {...field}
                    // Prevents controlled / uncontrolled console warning message
                    value={field.value ?? ''}
                    disabled={isDisabled || field.disabled}
                    data-1p-ignore={disablePasswordManager}
                    onFocus={onFocus}
                    onBlur={() => {
                      field.onBlur();
                      onBlur();
                    }}
                  />
                </FormControl>
              </Stack>
            </FormItem>

            <FormMessage className="w-full text-right" />
          </Stack>
        );
      }}
    />
  );
};
